import {
  UserEmailErrorResponse,
  UserEmailSuccessResponse,
  UserSendEmailResponse,
} from 'sources/model/user-source-send-email-response';
import axios from 'axios';
import { User, UserV2 } from 'models';
import { BffResponseObject, ErrorResponse } from 'sources/model/response';
import { IUserRelationship } from './model/program-relationships';

export class UserSource {
  public async fetchPrograms(
    email: string,
    token: string,
    showHidden: boolean,
  ): Promise<BffResponseObject<IUserRelationship>> {
    return await axios
      .get(`/api/v2/users/${email}/programs`, {
        params: {
          token,
          showHidden,
        },
      })
      .then((response) =>
        Promise.resolve({
          success: true,
          error: null,
          resBody: response.data.body,
        }),
      )
      .catch((error) => Promise.reject(error));
  }

  public async createUser(user: User): Promise<BffResponseObject<ErrorResponse<string>>> {
    let resOk = false;
    let resStatusText = null;

    return await fetch('/api/v1/users/', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(user),
    })
      .then((response) => {
        resOk = response.ok;
        resStatusText = response.statusText;

        return response.json();
      })
      .then((data) => {
        if (resOk) {
          return {
            success: true,
            error: null,
            resBody: null,
          };
        } else {
          return {
            success: false,
            error: resStatusText,
            resBody: data,
          };
        }
      })
      .catch((err) => {
        return {
          success: false,
          error: err.message,
          resBody: null,
        };
      });
  }

  public async createUserV2(user: UserV2): Promise<BffResponseObject<ErrorResponse<string>>> {
    let resStatusText = null;

    return await fetch('/api/v2/users/', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(user),
    })
      .then((response) => {
        resStatusText = response.statusText;

        return response.json();
      })
      .then((data) => {
        if (data.success) {
          return {
            success: true,
            error: null,
            resBody: null,
          };
        } else {
          return {
            success: false,
            error: resStatusText,
            resBody: data.body,
          };
        }
      })
      .catch((err) => {
        return {
          success: false,
          error: err.message,
          resBody: null,
        };
      });
  }

  public async sendEmail(email: string, locale: string, emailType: string): Promise<UserSendEmailResponse> {
    return await fetch(`/api/v1/users/${email}/email/${locale}/${emailType}`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    })
      .then((response) => {
        if (response.ok) {
          return {
            success: true,
            error: null,
          } as UserEmailSuccessResponse;
        } else {
          return {
            success: false,
            error: response.statusText,
          } as UserEmailErrorResponse;
        }
      })
      .catch((err) => {
        return {
          success: false,
          error: err.message,
        } as UserEmailErrorResponse;
      });
  }

  public async sendVerificationEmail(email: string, locale: string) {
    return this.sendEmail(email, locale, 'welcome');
  }

  public async sendForgotPasswordEmail(email: string, locale: string) {
    return this.sendEmail(email, locale, 'forgotpassword');
  }

  public async setPassword(password: string) {
    return await fetch('/api/v1/users/', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(password),
    })
      .then((response) => {
        if (response.ok) {
          return {
            success: true,
            error: null,
          };
        } else {
          return {
            success: false,
            error: response.statusText,
          };
        }
      })
      .catch((err) => {
        return {
          success: false,
          error: err.message,
        };
      });
  }

  public async verifyUser(email: string) {
    return await fetch(`/api/v1/users/${email}/verify`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    })
      .then((response) => {
        return response.json();
      })
      .then((verifyUserResponse) => ({
        success: true,
        body: verifyUserResponse,
      }))
      .catch((error) => ({
        success: false,
        error,
        body: null,
      }));
  }

  public async markLastLogin(email: string) {
    try {
      const response = await fetch(`/api/v1/users/${email}/lastLogin`, {
        method: 'PUT',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });
      const markLastLoginResponse = await response.json();

      return {
        success: true,
        body: markLastLoginResponse,
      };
    } catch (error) {
      return {
        success: false,
        error,
        body: null,
      };
    }
  }

  public async sendVerifyUpdateEmail(email: string, locale: string, firstName: string): Promise<void> {
    await axios.post(
      `/api/v1/users/${email}/email/simple/verifyEmail`,
      {
        username: email,
        firstName,
      },
      {
        params: {
          locale,
        },
      },
    );
  }
}
